import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { createOrder, validateCoupon, verifyPayment, fetchShippingOptions } from '../lib/api';
import { toast } from 'sonner';
import { ArrowRight, Tag, Truck, Lock, CheckCircle2, X, Info, CreditCard, Wallet } from 'lucide-react';
import SeoHelmet from '../components/SeoHelmet';
import Logo from '../components/Logo';

// US states (50 + DC) and Canadian provinces
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];
const CA_PROVINCES = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],
  ['NL','Newfoundland and Labrador'],['NS','Nova Scotia'],['NT','Northwest Territories'],
  ['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],
  ['SK','Saskatchewan'],['YT','Yukon'],
];

const emptyAddr = {
  full_name: '', email: '', phone: '',
  line1: '', line2: '', city: '',
  state: '', pincode: '', country: 'United States',
};

const PAYMENT_METHODS = [
  { id: 'stripe',   label: 'Stripe',   desc: 'Credit / Debit — Visa, Mastercard, Amex, Discover', icon: CreditCard, badge: 'MOCKED' },
  { id: 'paypal',   label: 'PayPal',   desc: 'PayPal Balance, Card or Pay in 4',                  icon: Wallet,      badge: 'MOCKED' },
];

const Checkout = () => {
  const { items, totals, clearCart } = useCart();
  const { format, code: currencyCode } = useCurrency();
  const [addr, setAddr] = useState(emptyAddr);
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShip, setSelectedShip] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const nav = useNavigate();

  const isCanada = addr.country === 'Canada';
  const stateOptions = isCanada ? CA_PROVINCES : US_STATES;
  const zipLabel = isCanada ? 'Postal Code' : 'ZIP Code';
  const stateLabel = isCanada ? 'Province' : 'State';
  const zipPlaceholder = isCanada ? 'A1A 1A1' : '10001';
  const zipPattern = isCanada ? '^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$' : '^\\d{5}(-\\d{4})?$';

  useEffect(() => { if (items.length === 0) nav('/shop'); }, [items.length, nav]);

  // Reset state selection when the country changes
  useEffect(() => { setAddr(a => ({ ...a, state: '', pincode: '' })); /* eslint-disable-next-line */ }, [addr.country]);

  // Fetch shipping options whenever ZIP/state/subtotal changes
  useEffect(() => {
    fetchShippingOptions({ pincode: addr.pincode || undefined, subtotal: totals.subtotal, state: addr.state || undefined })
      .then(r => {
        setShippingOptions(r.options || []);
        if (!selectedShip && r.options?.length) {
          const free = r.options.find(o => o.kind === 'free');
          setSelectedShip(free || r.options[0]);
        }
      })
      .catch(() => setShippingOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.pincode, addr.state, addr.country, totals.subtotal]);

  const discount = coupon?.discount || 0;
  const shipping_fee = selectedShip ? (selectedShip.kind === 'free' ? 0 : selectedShip.effective_price ?? selectedShip.price) : 0;
  // Sales tax preview — actual tax computed authoritatively by the server (0% by default for US).
  const taxable = Math.max(0, totals.subtotal - discount);
  const tax_preview = 0;
  const total = Math.max(0, totals.subtotal - discount + shipping_fee + tax_preview);

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const r = await validateCoupon(couponCode, totals.subtotal);
      if (r.valid) { setCoupon(r); toast.success(r.message); } else { setCoupon(null); toast.error(r.message); }
    } catch { toast.error('Coupon check failed'); }
  };

  const canProceed = ['full_name', 'email', 'phone', 'line1', 'city', 'state', 'pincode'].every(k => (addr[k] || '').trim());

  const placeOrder = async () => {
    if (!canProceed) { toast.error('Please fill all shipping fields'); return; }
    if (!selectedShip) { toast.error('Please select a shipping method'); return; }
    setProcessing(true);
    try {
      const payload = {
        items: items.map(it => ({ product_id: it.id, variant_id: it.variant_id, variant_label: it.variant_label, name: it.name, image: it.image, price: it.price, qty: it.qty, verse: it.verse, customization: it.customization })),
        shipping_address: addr,
        coupon_code: coupon?.code,
        shipping_option_id: selectedShip.id,
        payment_provider: paymentProvider,
      };
      const res = await createOrder(payload);
      toast.loading(`Processing ${paymentProvider.toUpperCase()} payment (mocked)...`, { id: 'pay' });
      await new Promise(r => setTimeout(r, 1200));
      const verifyBody = { order_number: res.order.order_number, provider: paymentProvider };
      if (paymentProvider === 'razorpay') verifyBody.razorpay_order_id = res.payment.order_id;
      if (paymentProvider === 'stripe') verifyBody.stripe_session_id = res.payment.session_id;
      if (paymentProvider === 'paypal') verifyBody.paypal_order_id = res.payment.paypal_order_id;
      const verified = await verifyPayment(verifyBody);
      toast.dismiss('pay');
      if (verified.ok) {
        clearCart();
        toast.success(`Payment successful via ${paymentProvider.toUpperCase()}!`);
        nav(`/order/${res.order.order_number}`);
      } else toast.error('Payment failed');
    } catch (e) {
      toast.dismiss('pay');
      toast.error(e?.response?.data?.detail || 'Order failed');
    } finally { setProcessing(false); }
  };

  const inputClass = 'mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-5 py-3 text-sm outline-none focus:border-[#D4A63A]';

  return (
    <main className="pt-6 pb-16 min-h-screen">
      <SeoHelmet overrides={{ title: 'Checkout | Wear The Hope with Ben', robots: 'noindex,follow' }} />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/" aria-label="Wear The Hope with Ben home"><Logo variant="dark" className="h-10" /></Link>
          <span className="text-xs text-neutral-400">/</span>
          <span className="font-serif-display text-3xl">Checkout</span>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 space-y-8">
            <div className="bg-white rounded-2xl border border-[#E7DCC7] p-6 md:p-8">
              <h2 className="font-serif-display text-2xl mb-6">Shipping Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block md:col-span-2">
                  <span className="text-[11px] uppercase tracking-widest text-neutral-600">Country</span>
                  <select value={addr.country} onChange={e => setAddr({ ...addr, country: e.target.value })} className={inputClass} data-testid="checkout-country">
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Full name</span><input value={addr.full_name} onChange={e => setAddr({ ...addr, full_name: e.target.value })} className={inputClass} data-testid="checkout-fullname" /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Email</span><input type="email" value={addr.email} onChange={e => setAddr({ ...addr, email: e.target.value })} className={inputClass} data-testid="checkout-email" /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Phone</span><input value={addr.phone} onChange={e => setAddr({ ...addr, phone: e.target.value })} placeholder={isCanada ? '+1 (416) 555-0100' : '+1 (555) 123-4567'} className={inputClass} data-testid="checkout-phone" /></label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Street address</span><input value={addr.line1} onChange={e => setAddr({ ...addr, line1: e.target.value })} placeholder="123 Main St" className={inputClass} data-testid="checkout-line1" /></label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Apartment / Suite (optional)</span><input value={addr.line2 || ''} onChange={e => setAddr({ ...addr, line2: e.target.value })} placeholder="Apt 4B" className={inputClass} data-testid="checkout-line2" /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">City</span><input value={addr.city} onChange={e => setAddr({ ...addr, city: e.target.value })} className={inputClass} data-testid="checkout-city" /></label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest text-neutral-600">{stateLabel}</span>
                  <select value={addr.state} onChange={e => setAddr({ ...addr, state: e.target.value })} className={inputClass} data-testid="checkout-state">
                    <option value="">Select {stateLabel.toLowerCase()}...</option>
                    {stateOptions.map(([code, name]) => (<option key={code} value={code}>{code} — {name}</option>))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-[11px] uppercase tracking-widest text-neutral-600">{zipLabel}</span>
                  <input value={addr.pincode} onChange={e => setAddr({ ...addr, pincode: e.target.value.toUpperCase() })} placeholder={zipPlaceholder} pattern={zipPattern} className={inputClass} data-testid="checkout-zip" />
                </label>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E7DCC7] p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4"><Truck className="w-4 h-4 text-[#8A6A22]" /><h2 className="font-serif-display text-2xl">Shipping Method</h2></div>
              {shippingOptions.length === 0 ? (
                <div className="text-sm text-neutral-500">Enter your {zipLabel.toLowerCase()} to see shipping options.</div>
              ) : (
                <div className="space-y-2">
                  {shippingOptions.map(opt => (
                    <label key={opt.id} className={`flex items-center justify-between gap-3 p-4 border rounded-xl cursor-pointer transition-colors ${selectedShip?.id === opt.id ? 'border-[#D4A63A] bg-[#F8F4EC]' : 'border-[#E7DCC7] hover:border-[#D4A63A]'}`}>
                      <div className="flex items-start gap-3">
                        <input type="radio" name="ship" checked={selectedShip?.id === opt.id} onChange={() => setSelectedShip(opt)} className="accent-[#D4A63A] mt-1" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{opt.name}</span>
                            {opt.kind === 'pickup' && <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Pickup</span>}
                            {opt.kind === 'free' && <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Free</span>}
                            {opt.kind === 'express' && <span className="text-[10px] uppercase tracking-widest bg-[#F8F4EC] text-[#8A6A22] px-2 py-0.5 rounded">Express</span>}
                          </div>
                          <div className="text-xs text-neutral-500 mt-0.5">{opt.kind === 'pickup' ? opt.pickup_address : `Delivery in ${opt.eta_days} business days`}</div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-[#8A6A22]">{(opt.effective_price ?? opt.price) === 0 ? 'Free' : format(opt.effective_price ?? opt.price)}</div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-[#E7DCC7] p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4"><Lock className="w-4 h-4 text-[#8A6A22]" /><h2 className="font-serif-display text-2xl">Payment Method</h2></div>
              <div className="grid sm:grid-cols-2 gap-3">
                {PAYMENT_METHODS.map(pm => {
                  const I = pm.icon;
                  const active = paymentProvider === pm.id;
                  return (
                    <button key={pm.id} onClick={() => setPaymentProvider(pm.id)} className={`flex items-center gap-3 p-4 border rounded-xl text-left transition-colors ${active ? 'border-[#D4A63A] bg-[#F8F4EC]' : 'border-[#E7DCC7] hover:border-[#D4A63A]'}`} data-testid={`payment-${pm.id}`}>
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-[#E7DCC7]"><I className="w-4 h-4 text-[#8A6A22]" /></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{pm.label}</span>
                          {pm.badge && <span className="text-[9px] uppercase tracking-widest bg-yellow-50 text-yellow-800 px-2 py-0.5 rounded">{pm.badge}</span>}
                        </div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">{pm.desc}</div>
                      </div>
                      {active && <CheckCircle2 className="w-4 h-4 text-[#8A6A22]" />}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-neutral-500 bg-[#F8F4EC] rounded-lg p-3"><Info className="w-3.5 h-3.5 mt-0.5 text-[#8A6A22]" /> All online payments are MOCKED. Add live Stripe / PayPal keys to the backend to enable real transactions.</div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-24 bg-white rounded-2xl border border-[#E7DCC7] p-6">
              <h2 className="font-serif-display text-2xl mb-4">Order Summary</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {items.map(it => (
                  <div key={(it.variant_id ? `${it.id}::${it.variant_id}` : it.id)} className="flex gap-3">
                    <img src={it.image} alt="" className="w-14 h-16 rounded object-cover" />
                    <div className="flex-1 text-sm">
                      <div className="font-medium line-clamp-2">{it.name}</div>
                      {it.variant_label && <div className="text-[11px] text-neutral-500">{it.variant_label}</div>}
                      <div className="text-xs text-neutral-500">Qty: {it.qty}</div>
                    </div>
                    <div className="text-sm font-semibold">{format(it.price * it.qty)}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-2">
                <Tag className="w-4 h-4 text-[#8A6A22]" />
                <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A] font-mono" />
                <button onClick={applyCoupon} className="bg-[#213A60] hover:bg-[#D4A63A] text-white text-xs uppercase tracking-widest px-4 py-2 rounded-full transition-colors">Apply</button>
              </div>
              {coupon && (
                <div className="mt-2 flex items-center justify-between bg-[#F8F4EC] rounded-lg px-3 py-2 text-xs">
                  <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> {coupon.code} applied</span>
                  <button onClick={() => { setCoupon(null); setCouponCode(''); }} className="text-neutral-500 hover:text-red-500"><X className="w-3 h-3" /></button>
                </div>
              )}

              <div className="mt-5 pt-5 border-t border-[#E7DCC7] space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span>{format(totals.subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-[#8A6A22]"><span>Discount</span><span>-{format(discount)}</span></div>}
                <div className="flex justify-between"><span className="text-neutral-600">Shipping</span><span>{shipping_fee === 0 ? 'Free' : format(shipping_fee)}</span></div>
                <div className="flex justify-between text-xs text-neutral-600"><span>Sales tax (est.)</span><span>Calculated at final step</span></div>
                <div className="flex justify-between text-lg font-serif-display pt-3 border-t border-[#E7DCC7]"><span>Total</span><span className="text-[#8A6A22]" data-testid="checkout-total">{format(total)}</span></div>
                {currencyCode !== 'USD' && (
                  <div className="text-[10px] text-neutral-500">Displayed in {currencyCode}. Charged in USD at today&apos;s rate.</div>
                )}
                <div className="text-[10px] text-neutral-500">Applicable sales tax computed on server</div>
              </div>

              <button disabled={processing || !canProceed} onClick={placeOrder} className="mt-6 w-full bg-[#213A60] hover:bg-[#D4A63A] disabled:bg-neutral-300 text-white py-4 rounded-full text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-colors" data-testid="checkout-place-order">
                {processing ? 'Processing...' : (<>Pay via {PAYMENT_METHODS.find(p => p.id === paymentProvider)?.label} · {format(total)} <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-neutral-500 text-center inline-flex items-center gap-2 justify-center w-full"><Lock className="w-3 h-3" /> Secure checkout · SSL encrypted</div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Checkout;
